import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AuthProvider } from "@/contexts/AuthContext";
import { AccountProvider } from "@/contexts/AccountContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import Layout from "@/components/Layout";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import Logout from "@/pages/auth/Logout";
import Home from "@/pages/Home";
import Terms from "@/pages/policy/Terms";
import Privacy from "@/pages/policy/Privacy";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import MyCard from "@/pages/card/MyCard";
import CardHistory from "@/pages/card/CardHistory";
import CardLock from "@/pages/card/CardLock";
import CardUnlock from "@/pages/card/CardUnlock";
import CardStolen from "@/pages/card/CardStolen";
import CardActivation from "@/pages/card/CardActivation";
import CardPinSetup from "@/pages/card/CardPinSetup";
import Profile from "@/pages/Profile";
import Help from "@/pages/Help";
import HelpDetail from "@/pages/help/HelpDetail";
import SupportTicket from "@/pages/SupportTicket";
import SupportTicketDetail from "@/pages/SupportTicketDetail";
import InputAddress from "@/pages/kyc/InputAddress";
import KycOnfido from "@/pages/kyc/KycOnfido";
import KycComplete from "@/pages/kyc/KycComplete";
import KycFailed from "@/pages/kyc/KycFailed";
import OxPaySetup from "@/components/OxPaySetup";
import Guide from "@/pages/Guide";
import RegisterUser from "@/pages/RegisterUser";
import EditUser from "@/pages/EditUser";
import CreateWallet from "@/pages/CreateWallet";

// Admin imports
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminSettings from "@/pages/admin/AdminSettings";
import SiteSettings from "@/pages/admin/SiteSettings";
import AdminSetup from "@/pages/admin/AdminSetup";
import AdminSupportTickets from "@/pages/admin/SupportTickets";
import AdminCards from "@/pages/admin/Cards";
import AdminUsers from "@/pages/admin/Users";
import AdminPrefund from "@/pages/admin/Prefund";
import AdminTopups from "@/pages/admin/Topups";

function App() {
  const [currentVersion, setCurrentVersion] = useState(null);

  useEffect(() => {
    // Load initial version
    fetch("/version.json?" + Date.now())
      .then((res) => res.json())
      .then((data) => {
        setCurrentVersion(data.version);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!currentVersion) return;

    // Check for new version every 30 seconds
    const interval = setInterval(async () => {
      try {
        // console.log("Checking for new version, current:", currentVersion);
        const res = await fetch("/version.json?" + Date.now());
        const data = await res.json();
        // console.log("Fetched version:", data.version);
        if (data.version !== currentVersion) {
          // console.log("New version detected! Reloading...");
          window.location.reload(true); // Force reload from server, not cache
        }
      } catch (error) {
        console.error("Version check failed:", error);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [currentVersion]);

  return (
    <Router>
      <AuthProvider>
        <AccountProvider>
          <ScrollToTop />
          <Layout>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/guide" element={<Guide />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/setup" element={<OxPaySetup />} />
              <Route path="/admin-setup" element={<AdminSetup />} />
              <Route path="/home" element={<Home />} />
              <Route path="/mycard" element={<MyCard />} />
              <Route path="/card-lock" element={<CardLock />} />
              <Route path="/card-unlock" element={<CardUnlock />} />
              <Route path="/card-stolen" element={<CardStolen />} />
              <Route path="/card-activation" element={<CardActivation />} />
              <Route path="/card-pin-setup" element={<CardPinSetup />} />
              <Route path="/history" element={<CardHistory />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/help" element={<Help />} />
              <Route path="/help/:id" element={<HelpDetail />} />
              <Route path="/support-ticket" element={<SupportTicket />} />
              <Route
                path="/support-ticket/:ticketId"
                element={<SupportTicketDetail />}
              />
              <Route path="/register-user" element={<RegisterUser />} />
              <Route path="/edit-user" element={<EditUser />} />
              <Route path="/input-address" element={<InputAddress />} />
              <Route path="/kyc-onfido" element={<KycOnfido />} />
              <Route path="/kyc-complete" element={<KycComplete />} />
              <Route path="/kyc-failed" element={<KycFailed />} />
              <Route path="/create-wallet" element={<CreateWallet />} />

              {/* Admin routes: auth required; AdminLayout enforces admin + adminMode */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="cards" element={<AdminCards />} />
                <Route path="prefund" element={<AdminPrefund />} />
                <Route path="topups" element={<AdminTopups />} />
                <Route path="kyc" element={<AdminDashboard />} />
                <Route path="support" element={<AdminSupportTickets />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="site-settings" element={<SiteSettings />} />
              </Route>
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </Layout>
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
        </AccountProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
